import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, Loader2, Receipt, RefreshCw, TrendingDown, TrendingUp, Upload, Wallet,
} from "lucide-react";
import { billsRead, isFeatureOff } from "@/lib/billsNative";
import { useSession } from "@/contexts/SessionContext";

const money = (v, c = "INR") =>
  v == null ? "—" : `${c === "INR" ? "₹" : `${c} `}${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const monthName = (m) =>
  m ? new Date(`${m}-01T00:00:00Z`).toLocaleDateString("en-IN", { month: "long", year: "numeric", timeZone: "UTC" }) : "";

const Section = ({ title, action, children, testId }) => (
  <section className="space-y-3" data-testid={testId}>
    <div className="flex items-baseline justify-between gap-3">
      <h2 className="text-base md:text-lg font-semibold text-archival-primary tracking-tight">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const More = ({ to, label, testId }) => (
  <Link to={to} data-testid={testId} className="text-[13px] text-archival-secondary hover:text-archival-primary inline-flex items-center gap-1 transition-colors">
    {label} <ArrowRight size={13} />
  </Link>
);

const AttentionTile = ({ label, count, to, testId, tone = "default" }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`flex items-center justify-between gap-3 rounded-md border px-4 py-3 min-h-[60px] transition-colors ${
      count > 0 && tone === "urgent"
        ? "border-amber-300 bg-amber-50 hover:bg-amber-100"
        : "border-archival bg-archival-surface hover:bg-archival-base"
    }`}
  >
    <span className="text-[13px] text-archival-secondary leading-snug">{label}</span>
    <span className={`text-[20px] font-semibold tabular-nums ${count > 0 ? "text-archival-primary" : "text-archival-muted"}`}>
      {count ?? 0}
    </span>
  </Link>
);

export default function BillsHomeCockpit() {
  const { currentOutletId } = useSession();
  const [state, setState] = useState({ phase: "loading", data: null, error: null });

  const load = useCallback(async () => {
    if (!currentOutletId) return setState({ phase: "no-outlet", data: null, error: null });
    setState((s) => ({ ...s, phase: s.data ? "refreshing" : "loading" }));
    const r = await billsRead.homeSummary({});
    if (isFeatureOff(r) || (!r.ok && (r.status === 503 || r.status === 0)))
      return setState({ phase: "unavailable", data: null, error: r.data || null });
    if (!r.ok) return setState({ phase: "error", data: null, error: r.data || null });
    setState({ phase: "ready", data: r.data, error: null });
  }, [currentOutletId]);

  useEffect(() => { load(); }, [load]);

  if (state.phase === "no-outlet")
    return (
      <p className="text-[15px] text-archival-secondary" data-testid="home-no-outlet">
        Select an outlet to see what is happening in Bills.
      </p>
    );

  if (state.phase === "loading")
    return (
      <div className="flex items-center gap-2 text-archival-secondary text-[14px]" data-testid="home-loading">
        <Loader2 className="animate-spin" size={16} /> Loading your Bills summary…
      </div>
    );

  if (state.phase === "unavailable" || state.phase === "error")
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-6 max-w-xl" data-testid="home-unavailable">
        <div className="flex items-center gap-2 text-amber-900">
          <AlertTriangle size={16} />
          <h2 className="text-base font-semibold">Bills summary is temporarily unavailable</h2>
        </div>
        <p className="mt-2 text-[13px] text-amber-900/90">
          We couldn&apos;t reach the Bills datastore. Your existing Bills, Expenses and evidence have not been
          changed, and nothing has been sent downstream.
        </p>
        <button
          type="button"
          onClick={load}
          data-testid="home-retry"
          className="mt-4 inline-flex items-center gap-1.5 rounded px-3 py-2 text-[13px] font-medium border border-amber-300 hover:bg-amber-100 transition-colors"
        >
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );

  const d = state.data;
  const att = d.needs_attention;
  const p = d.purchases;
  const cur = p.currency;
  const attentionTotal =
    (att.bills_needing_decisions || 0) + (att.void_approvals || 0) + (att.corrections_pending || 0);
  const up = p.movement_percent != null && p.movement_percent >= 0;

  return (
    <div className="space-y-10 md:space-y-12 pb-16" data-testid="bills-home">
      <header className="space-y-1">
        <h1 className="text-4xl sm:text-5xl font-semibold text-archival-primary tracking-tight leading-[1.05]">
          Bills
        </h1>
        <p className="text-base md:text-lg text-archival-secondary">
          What you bought, from whom, at what price — and what needs you.
        </p>
      </header>

      <Section
        title={attentionTotal ? `Needs attention · ${attentionTotal}` : "Needs attention"}
        testId="home-attention"
        action={<More to="/bills?view=attention" label="Open queue" testId="home-attention-link" />}
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <AttentionTile label="Bills needing a decision" count={att.bills_needing_decisions} to="/bills?view=attention"
            testId="home-tile-attention" tone="urgent" />
          <AttentionTile label="Drafts in progress" count={att.drafts} to="/bills?view=drafts" testId="home-tile-drafts" />
          <AttentionTile label="Void approvals waiting" count={att.void_approvals} to="/bills?view=all"
            testId="home-tile-voids" tone="urgent" />
          <AttentionTile label="Corrections pending" count={att.corrections_pending} to="/bills?view=attention"
            testId="home-tile-corrections" />
        </div>
        {attentionTotal === 0 && (
          <p className="text-[13px] text-archival-muted" data-testid="home-attention-empty">
            Nothing needs your attention right now.
          </p>
        )}
      </Section>

      <div className="grid gap-10 md:gap-8 lg:grid-cols-2">
        <Section title="Purchases" testId="home-purchases">
          <div className="rounded-md border border-archival bg-archival-surface p-5 space-y-4">
            <div>
              <p className="text-[12px] uppercase tracking-wide text-archival-muted">{monthName(d.month)}</p>
              <p className="text-3xl font-semibold text-archival-primary tabular-nums mt-1" data-testid="home-purchase-value">
                {money(p.this_month_value, cur)}
              </p>
              <p className="text-[13px] text-archival-secondary mt-1">
                {p.this_month_bills} {p.this_month_bills === 1 ? "bill" : "bills"} confirmed
              </p>
            </div>
            <div className="flex items-center gap-2 border-t border-archival pt-3 text-[13px]">
              {p.movement_percent == null ? (
                <span className="text-archival-muted">No purchases in {monthName(d.previous_month)} to compare</span>
              ) : (
                <>
                  <span className={`inline-flex items-center gap-1 font-medium ${up ? "text-emerald-700" : "text-red-700"}`}
                    data-testid="home-purchase-movement">
                    {up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    {up ? "+" : ""}{p.movement_percent}%
                  </span>
                  <span className="text-archival-secondary">
                    vs {monthName(d.previous_month)} ({money(p.previous_month_value, cur)})
                  </span>
                </>
              )}
            </div>
            <p className="text-[11px] text-archival-muted">
              Purchase value from confirmed Bills. Not COGS, not P&amp;L — Accounts owns those.
            </p>
          </div>
        </Section>

        <Section title="Expenses" testId="home-expenses"
          action={<More to="/expenses" label="All expenses" testId="home-expenses-link" />}>
          <div className="rounded-md border border-archival bg-archival-surface p-5 space-y-4">
            <div>
              <p className="text-[12px] uppercase tracking-wide text-archival-muted">{monthName(d.month)}</p>
              <p className="text-3xl font-semibold text-archival-primary tabular-nums mt-1" data-testid="home-expense-total">
                {money(d.expenses.this_month_total, cur)}
              </p>
              <p className="text-[13px] text-archival-secondary mt-1">
                {d.expenses.count} {d.expenses.count === 1 ? "expense" : "expenses"}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 border-t border-archival pt-3 text-[13px]">
              <div>
                <p className="text-archival-muted text-[12px]">Cash</p>
                <p className="font-medium text-archival-primary tabular-nums">{money(d.expenses.cash, cur)}</p>
              </div>
              <div>
                <p className="text-archival-muted text-[12px]">Non-cash</p>
                <p className="font-medium text-archival-primary tabular-nums">{money(d.expenses.non_cash, cur)}</p>
              </div>
            </div>
          </div>
        </Section>
      </div>

      <Section title="Recent bills" testId="home-recent"
        action={<More to="/bills?view=all" label="All bills" testId="home-bills-link" />}>
        {d.recent_bills.length === 0 ? (
          <div className="rounded-md border border-dashed border-archival p-8 text-center" data-testid="home-recent-empty">
            <Receipt size={20} className="mx-auto text-archival-muted" />
            <p className="mt-2 text-[14px] font-medium text-archival-primary">No bills yet</p>
            <p className="text-[13px] text-archival-secondary">Confirmed supplier bills will appear here.</p>
            <Link to="/capture" className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-archival-primary underline">
              <Upload size={13} /> Capture a bill
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-archival rounded-md border border-archival bg-archival-surface overflow-hidden">
            {d.recent_bills.map((b) => (
              <li key={b.bill_id}>
                <Link to={`/bills/detail/${b.bill_id}`} data-testid={`home-bill-${b.bill_id}`}
                  className="flex items-center gap-4 px-4 py-3 hover:bg-archival-base transition-colors">
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-medium text-archival-primary truncate">{b.vendor_name || "Vendor pending"}</p>
                    <p className="text-[12px] text-archival-secondary truncate">
                      {b.bill_number || "No bill number"} · {b.bill_date || "—"}
                    </p>
                  </div>
                  <span className="text-[14px] font-medium text-archival-primary tabular-nums shrink-0">
                    {money(b.total_amount, b.currency || cur)}
                  </span>
                  <span className="text-[11px] uppercase tracking-wide text-archival-muted shrink-0 hidden sm:inline">
                    {(b.status || "").replace("_", " ")}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Vendor activity" testId="home-vendors"
        action={<More to="/vendors" label="All vendors" testId="home-vendors-link" />}>
        {d.vendors.top.length === 0 ? (
          <p className="text-[13px] text-archival-muted" data-testid="home-vendors-empty">
            No vendor purchase history yet.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {d.vendors.top.map((v) => (
              <Link key={v.vendor_id} to={`/vendors/${encodeURIComponent(v.vendor_id)}`}
                data-testid={`home-vendor-${v.vendor_id}`}
                className="rounded-md border border-archival bg-archival-surface px-4 py-3 hover:bg-archival-base transition-colors">
                <p className="text-[14px] font-medium text-archival-primary truncate">{v.vendor_name}</p>
                <p className="text-[12px] text-archival-secondary mt-0.5">
                  {money(v.purchase_value, cur)} · {v.bills} {v.bills === 1 ? "bill" : "bills"} · last {v.last_purchase || "—"}
                </p>
              </Link>
            ))}
          </div>
        )}
      </Section>

      <Section title="Price movement" testId="home-price">
        <div className="rounded-md border border-dashed border-archival px-5 py-4" data-testid="home-price-gap">
          <p className="text-[13px] text-archival-secondary">
            Purchase-price history is not available yet. Bills already stores every confirmed purchase price, so
            this is the next intelligence we can surface.
          </p>
        </div>
      </Section>

      <p className="text-[12px] text-archival-muted flex items-center gap-2">
        <Wallet size={12} /> Bills owns purchases and operating expenses. Inventory balances and accounting
        remain owned by Inventory and Accounts.
      </p>
    </div>
  );
}
